import {
	Component,
	OnDestroy,
	OnInit,
	inject,
	ViewEncapsulation,
	ChangeDetectionStrategy,
} from '@angular/core';
import { ColDef, SideBarDef } from 'ag-grid-community';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BankingPortfolioService } from '@lmc-app/core/services/banking-portfolio.service';
import { ProjectionService } from '@lmc-app/core/services/projection.service';
import { LoanAccountService } from '@lmc-app/core/services/loan-account.service';
import { CurrencyPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { FundingListToolPanelComponent } from '@lmc-app/custom-table/funding-list-tool-panel/funding-list-tool-panel.component';
import { provideDayjsDateAdapter } from '@lmc-app/shared/adapters/dayjs-date-adapter';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
} from '@angular/material/datepicker';
import { Store } from '@ngrx/store';
import { selectUser, State } from '@lmc-app/store';
import { User } from '@lmc-app/shared/interface/user.interface';
import { Subject, takeUntil } from 'rxjs';
import { Loading } from '../../applications/loading';

import { Dayjs } from 'dayjs';
import {
	MatFormField,
	MatLabel,
	MatInput,
	MatHint,
	MatSuffix,
} from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { CustomTableComponent } from '../../custom-table/custom-table.component';

export const MY_FORMATS = {
	parse: {
		dateInput: 'MM/YYYY',
	},
	display: {
		dateInput: 'MM/YYYY',
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	},
};

@Component({
	selector: 'app-funding-list-mom',
	templateUrl: './funding-list-mom.component.html',
	styleUrls: ['./funding-list-mom.component.scss'],
	providers: [provideDayjsDateAdapter(MY_FORMATS)],
	changeDetection: ChangeDetectionStrategy.Eager,
	encapsulation: ViewEncapsulation.None,
	imports: [
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		MatHint,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatButton,
		CustomTableComponent,
	],
})
export class FundingListMomComponent implements OnInit, OnDestroy {
	private readonly _onDestroy = new Subject<void>();

	inputDateForm = new FormGroup({
		input_date: new FormControl(null),
		target: new FormControl(null),
		num_business_day: new FormControl(null),
	});
	public columnDefs: ColDef[] = [
		{
			field: 'input_date',
			headerName: '',
			width: 150,
			pinned: 'left',
		},
		{
			headerName: 'SAHA CHQ',
			width: 130,
			valueGetter: (params) => {
				return this.currencyFormatter.transform(
					params.data['SAHA CHQ'],
					'CAD',
					'symbol-narrow'
				);
			},
		},
	];

	bankingPortfolios: IdName[] = [];
	loanAccountData = [];
	sum = {
		'SAHA CHQ': 0,
	};
	showLoader: boolean;
	showEmptyResults: boolean;
	userData: any;

	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _snackBar = inject(MatSnackBar);

	public sideBar: SideBarDef | string | string[] | boolean | null;

	public summaryData;

	constructor(
		private bankingPortfolioService: BankingPortfolioService,
		private projectionService: ProjectionService,
		private loanAccountService: LoanAccountService,
		private currencyFormatter: CurrencyPipe,
		public dialog: MatDialog,
		private store: Store<State>
	) {}

	ngOnInit(): void {
		this.showEmptyResults = false;
		this.setFundingAccount([]);
		this.inputDateForm.controls.input_date.setValue(new Date());
		// Subscribe to user from NgRx store
		this.store
			.select(selectUser)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User | null) => {
				if (user) {
					this.userData = user;
					this.getPortfolios();
					const inputDateValue = this.inputDateForm.controls.input_date.value;
					this.getProjection(
						inputDateValue.getFullYear(),
						inputDateValue.getMonth() + 1
					);
					this.getFundingAccount(
						inputDateValue.getFullYear(),
						inputDateValue.getMonth() + 1
					);
				}
			});
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	getPortfolios() {
		this.bankingPortfolioService.getBankingPortfolios().subscribe({
			next: (res) => {
				this.bankingPortfolios = res;
				this.columnDefs = [
					...this.columnDefs,
					...this.bankingPortfolios.map((bankingPortfolio) => ({
						headerName: bankingPortfolio.name,
						width: 120,
						valueGetter: (params) => {
							return this.currencyFormatter.transform(
								params.data[bankingPortfolio.name],
								'CAD',
								'symbol-narrow'
							);
						},
					})),
					{
						field: 'NO OF APPS',
						width: 100,
						headerName: 'NO OF APPS',
					},
					{
						field: 'LPP NQ',
						width: 100,
						headerName: 'LPP NQ',
						editable: true,
						valueSetter: (params) => {
							params.data['LPP NQ'] = Number(params.newValue);
							return true;
						},
						cellStyle: (params) => {
							let style = { color: 'black' };
							if (params.data['LPP NQ'] !== params.data.original_lpp_nq) {
								style = { color: 'red' };
							}
							return style;
						},
					},
					{
						field: 'LPP',
						width: 100,
						headerName: 'LPP',
					},
					{
						width: 130,
						headerName: 'TOTAL',
						valueGetter: (params) => {
							return this.currencyFormatter.transform(
								params.data['TOTAL'],
								'CAD',
								'symbol-narrow'
							);
						},
					},
					{
						width: 130,
						headerName: 'AGENCY REFERAL',
						valueGetter: (params) => {
							return this.currencyFormatter.transform(
								params.data['AGENCY REFERAL'],
								'CAD',
								'symbol-narrow'
							);
						},
					},
					{
						field: 'EXCEPTIONS',
						headerName: 'EXCEPTIONS',
					},
				];
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	getProjection(year, month) {
		this.projectionService.getProjection(year, month).subscribe({
			next: (res) => {
				this.inputDateForm.controls.target.setValue(res?.target);
				this.inputDateForm.controls.num_business_day.setValue(
					res?.num_business_day
				);
			},
			error: (error) => {
				console.error(error);
			},
		});
	}

	setMonthAndYear(
		normalizedMonthAndYear: Dayjs,
		datepicker: MatDatepicker<Dayjs>
	) {
		const year = normalizedMonthAndYear.year();
		const month = normalizedMonthAndYear.month() + 1;
		const inputDate = `${year}-${month.toString().padStart(2, '0')}-02`;
		this.inputDateForm.controls.input_date.setValue(new Date(inputDate));
		this.getProjection(year, month);
		this.getFundingAccount(year, month);
		datepicker.close();
	}

	onCellValueChanged(e) {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving' },
		});
		const inputDateValue = this.inputDateForm.controls.input_date.value;
		console.log(e.data);
		this.loanAccountService.updateMOMLoanAccount(e.data).subscribe({
			next: (res) => {
				this.getFundingAccount(
					inputDateValue.getFullYear(),
					inputDateValue.getMonth() + 1
				);
				this._snackBar.open(
					`Loan account for ${res.name} is updated.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
			},
			error: (error) => {
				this.getFundingAccount(
					inputDateValue.getFullYear(),
					inputDateValue.getMonth() + 1
				);
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error &&
						error.error.message &&
						(error.error.message.name || error.error.message)) ||
						'Error updating loan account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		});
	}

	saveProjection() {
		this._bottomSheet.open(Loading, {
			disableClose: true,
			data: { message: 'Saving projection' },
		});
		const projection = this.convertFormToJson();
		this.projectionService.upsertProjection(projection).subscribe({
			next: (res) => {
				this.setSummaryData();
				this._snackBar.open(
					`Projection for ${res.year}-${res.month.toString().padStart(2, '0')} is updated.`,
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
				this._bottomSheet.dismiss();
			},
			error: (error) => {
				this._bottomSheet.dismiss();
				console.error(error);
				this._snackBar.open(
					(error.error &&
						error.error.message &&
						(error.error.message.name || error.error.message)) ||
						'Error updating account.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_SHORT
				);
			},
		});
	}

	convertFormToJson() {
		const projection = this.inputDateForm.getRawValue();
		projection['year'] = projection.input_date.getFullYear();
		projection['month'] = projection.input_date.getMonth() + 1;
		delete projection.input_date;
		return projection;
	}

	getFundingAccount(inputYear, inputMonth) {
		this.showLoader = true;
		this.loanAccountService
			.getMOMLoanAccounts(inputYear, inputMonth)
			.subscribe((res: any) => {
				if (res.length) {
					this.setFundingAccount(res);
					this.showLoader = false;
					this.showEmptyResults = false;
				} else {
					this.showLoader = false;
					this.setFundingAccount([]);
					this.showEmptyResults = true;
				}
			});
	}

	setFundingAccount(data) {
		this.loanAccountData = data;
		this.sum = {
			'SAHA CHQ': 0,
		};
		this.bankingPortfolios.forEach((portfolio) => {
			this.sum[portfolio.name] = data.reduce(
				(acc, curr) => acc + curr[portfolio.name],
				0
			);
		});
		this.sum['SAHA CHQ'] = data.reduce(
			(acc, curr) => acc + curr['SAHA CHQ'],
			0
		);
		this.sum['NO OF APPS'] = data.reduce(
			(acc, curr) => acc + curr['NO OF APPS'],
			0
		);
		this.sum['LPP NQ'] = data.reduce((acc, curr) => acc + curr['LPP NQ'], 0);
		this.sum['LPP'] = data.reduce((acc, curr) => acc + curr['LPP'], 0);
		this.sum['TOTAL'] = data.reduce((acc, curr) => acc + curr['TOTAL'], 0);
		this.sum['AGENCY REFERAL'] = data.reduce(
			(acc, curr) => acc + curr['AGENCY REFERAL'],
			0
		);
		this.setSummaryData();
	}

	setSummaryData() {
		if (
			this.inputDateForm.controls.target.value &&
			this.inputDateForm.controls.num_business_day.value &&
			Number(
				this.inputDateForm.controls.num_business_day.value -
					this.loanAccountData.length
			) > 0
		) {
			this.sum['DAILY TARGET'] =
				(Number(this.inputDateForm.controls.target.value) - this.sum['TOTAL']) /
				(Number(this.inputDateForm.controls.num_business_day.value) -
					this.loanAccountData.length);
		} else {
			delete this.sum['DAILY TARGET'];
		}
		const summaryData = [];
		for (const [label, value] of Object.entries(this.sum)) {
			switch (label) {
				case 'NO OF APPS':
				case 'LPP NQ':
				case 'LPP':
					summaryData.push({
						label,
						value,
					});
					break;
				default:
					summaryData.push({
						label,
						value: this.currencyFormatter.transform(
							value,
							'CAD',
							'symbol-narrow'
						),
						primary: label === 'TOTAL',
					});
					break;
			}
		}
		this.sideBar = {
			toolPanels: [
				{
					id: 'filters',
					labelDefault: 'Filters',
					labelKey: 'filters',
					iconKey: 'filter',
					toolPanel: 'agFiltersToolPanel',
				},
				{
					id: 'columns',
					labelDefault: 'Columns',
					labelKey: 'columns',
					iconKey: 'columns',
					toolPanel: 'agColumnsToolPanel',
					toolPanelParams: {
						suppressPivotMode: true,
						suppressValues: true,
						suppressRowGroups: true,
					},
				},
				{
					id: 'customStats',
					labelDefault: 'Summary',
					labelKey: 'customStats',
					iconKey: 'chart',
					width: 270,
					toolPanel: FundingListToolPanelComponent,
					toolPanelParams: {
						summaryData,
					},
				},
			],
			defaultToolPanel: 'customStats',
		};
	}
}
